import OrderApi from '@apis/order/OrderApi';
import queries from '@apis/queries';
import { datadogRum } from '@datadog/browser-rum';
import useFingerPay from '@hooks/order/useFingerPay';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { CreateOrderRequest, CreateOrderResponse } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@router/stackflow';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCookies } from 'react-cookie';

function usePrepareOrder() {
  const state = useCalculatedRequest();
  const projectId = useProjectId();
  const [_, setCookie] = useCookies(['shopId', 'shopName', 'tempOrder']);
  const toast = useToast();
  const { push, pop } = useFlow();
  const queryClient = useQueryClient();
  const { startLoading, stopLoading } = useLoading();
  const openFingerPay = useFingerPay();

  const logToDatadog = () => {
    if (import.meta.env.MODE !== 'development') {
      datadogRum.addAction('orderCheckout', {
        items: state.orderLines.map((it) => it.shopItemId),
        totalAmount: state.totalAmount,
        paymentAmount: state.paymentAmount,
        paymentMethod: state.paymentMethod,
      });
    }
  };

  const handleKicc = (checkoutUrl: string) => {
    setCookie('shopId', state.shopId, { path: '/' });
    setCookie('shopName', state.shopName, { path: '/' });
    // 주문 취소 후 복구에 필요
    setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
    stopLoading();
    window.location.href = checkoutUrl;
  };

  const handleFingerPay = (
    response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString'>>,
  ) => {
    setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
    openFingerPay({
      ...response,
      paymentAmount: orderWithTotalAmount(state).paymentAmount,
      itemName: state.orderLines
        .map((orderLine) => orderLine.itemName)
        .join(' + '),
      returnUri: '/order/payment/finger-pay',
    });
  };

  return async () => {
    startLoading();
    const request: CreateOrderRequest = { ...orderWithTotalAmount(state) };
    request.returnUrl = `${import.meta.env.VITE_BASE_URL}/purchase/complete`;
    // 옵션 그룹별로 분리된 것 통합
    request.orderLines = request.orderLines.map((orderLine) => {
      const { defaultOptions, personalOptions } = orderLine;
      const options = [...defaultOptions, ...personalOptions]
        .map((optionGroup) => optionGroup.options)
        .flat();
      const optionIds = [...new Set(options.map(
        (opt) => opt.shopOptionId,
      ))];
      const newOptions = optionIds.map((optionId) => {
        const foundOptions = options.filter((opt) => opt.shopOptionId === optionId);
        const newOption = {
          ...foundOptions[0],
          quantity: foundOptions.map((opt) => opt.quantity)
            .reduce((prev, curr) => prev + curr, 0),
          totalAmount: foundOptions.map((opt) => opt.totalAmount)
            .reduce((prev, curr) => prev + curr, 0),
        };
        return newOption;
      });
      // @ts-ignore
      orderLine.options = newOptions;
      return orderLine;
    });
    request.discounts = request.discounts || [];
    try {
      // 주문금액 0원일 경우 pg 미사용
      if (state.paymentAmount === 0) {
        const { orderNo } = await OrderApi.createFreeOrder({ ...request, projectId });
        logToDatadog();
        queryClient.invalidateQueries({ queryKey: queries.orders._def });
        queryClient.invalidateQueries({ queryKey: queries.user._def });
        pop(4, { animate: false });
        push('OrderLogsActivity', { duration: 'ALL' }, { animate: false });
        push('OrderLogActivity', { orderNo });
        stopLoading();
      } else {
        const {
          checkoutUrl, orderNo, mallId, requestedAt, hashString,
        } = await OrderApi.createOrder({ ...request, projectId });
        logToDatadog();
        if (checkoutUrl) {
          handleKicc(checkoutUrl);
        } else if (orderNo && mallId && requestedAt && hashString) {
          handleFingerPay({
            orderNo, mallId, requestedAt, hashString,
          });
        }
      }
    } catch (e) {
      const axiosError = e as AxiosError<ErrorResponse>;
      stopLoading();
      toast.error(axiosError.response?.data.message || '주문 생성에 실패했습니다.');
    }
  };
}

export default usePrepareOrder;
